import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Gem, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { raritySurfaceStyle } from '../../../utils/rarity'
import { parseSkinName } from '../../../utils/formatters'
import { hapticImpact, hapticSelectionChanged } from '../../../utils/haptic'
import useAudioManager from '../../../hooks/useAudioManager'
import useUiOverlayStore from '../../../store/useUiOverlayStore'

const UpgradeSelectionSheet = ({ mode, onClose, inventory, source, groups, multiplier, target, onSource, onMultiplier, onTarget }) => {
  const { t, i18n } = useTranslation()
  const { playSound } = useAudioManager({ select: 'select' })
  const closeRef = useRef(null)
  const dialogRef = useRef(null)
  const [draftKey, setDraftKey] = useState(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const { pushOverlay, popOverlay } = useUiOverlayStore.getState()
  const keys = Object.keys(groups).filter((key) => groups[key].length > 0).sort((a, b) => Number(a) - Number(b))
  const items = mode === 'source' ? inventory : groups[multiplier] || []
  const selectedKey = keys.includes(draftKey) ? draftKey : keys.includes(multiplier) ? multiplier : keys[0]
  const selectedIndex = Math.max(0, keys.indexOf(selectedKey))
  const progress = keys.length > 1 ? selectedIndex / (keys.length - 1) * 100 : 0
  const riskColor = Number(selectedKey) <= 5 ? 'var(--info)' : Number(selectedKey) <= 10 ? '#FBBF24' : Number(selectedKey) <= 50 ? '#FB923C' : 'var(--error)'
  const markers = [keys[0], '10', '50', keys[keys.length - 1]].filter((key, index, values) => key && keys.includes(key) && values.indexOf(key) === index)
  const commitMultiplier = () => {
    if (selectedKey && selectedKey !== multiplier) onMultiplier(selectedKey)
    setDraftKey(null)
  }
  const changeMultiplier = (event) => {
    const nextKey = keys[Number(event.target.value)]
    if (nextKey !== selectedKey) hapticSelectionChanged()
    setDraftKey(nextKey)
  }

  useEffect(() => {
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    pushOverlay()
    closeRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCloseRef.current()
      if (event.key !== 'Tab') return
      const focusable = dialogRef.current?.querySelectorAll('button:not([disabled])')
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      popOverlay()
      previousFocus?.focus?.()
    }
  }, [pushOverlay, popOverlay])

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} aria-hidden="true" />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="upgrade-sheet-title" className="relative flex max-h-[88dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-dark-primary shadow-2xl sm:rounded-3xl">
        <div className="shrink-0 border-b border-white/10 px-4 pb-3 pt-3">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20 sm:hidden" />
          <div className="flex items-center justify-between gap-3">
            <h2 id="upgrade-sheet-title" className="text-xl font-bold text-white">{t(mode === 'source' ? 'upgrader.step1Title' : 'upgrader.step2SelectTarget')}</h2>
            <button ref={closeRef} type="button" onClick={onClose} aria-label={t('common.close')} className="rounded-xl border border-white/10 bg-surface p-2.5 text-text-secondary active:scale-[0.98]"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {mode === 'target' && keys.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-surface px-4 py-3" style={{ '--risk-color': riskColor, '--risk-progress': `${progress}%` }}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">{t('upgrader.step2Title')}</span>
                <strong className="text-xl tabular-nums" style={{ color: riskColor }}>×{selectedKey}</strong>
              </div>
              <div className="relative mt-1 h-11">
                <input type="range" min="0" max={keys.length - 1} step="1" value={selectedIndex} onChange={changeMultiplier} onPointerUp={commitMultiplier} onKeyUp={commitMultiplier} onBlur={commitMultiplier} aria-label={t('upgrader.step2Title')} aria-valuetext={`×${selectedKey}`} disabled={keys.length < 2} className="upgrade-risk-slider absolute inset-0 h-11 w-full focus-visible:rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-info disabled:cursor-default" />
              </div>
              <div className="relative mx-2.5 h-4 text-[10px] text-text-secondary">
                {markers.map((key) => <span key={key} style={{ left: `${keys.indexOf(key) / Math.max(1, keys.length - 1) * 100}%`, transform: key === keys[0] ? undefined : key === keys[keys.length - 1] ? 'translateX(-100%)' : 'translateX(-50%)' }} className={`absolute top-0 ${key === keys[0] || key === keys[keys.length - 1] ? '' : 'hidden sm:block'}`}>×{key}</span>)}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {items.map((skin) => {
              const selected = String((mode === 'source' ? source : target)?.id) === String(skin.id)
              const { title, subtitleCompact } = parseSkinName(skin.name)
              const price = Number(skin.price ?? skin.cost ?? 0).toLocaleString(i18n.language)
              return <button key={skin.id} type="button" aria-label={`${skin.name}, ${price}`} onClick={() => { playSound('select'); hapticImpact('light'); (mode === 'source' ? onSource : onTarget)(skin); onClose() }} className={`relative flex min-w-0 flex-col rounded-xl border bg-surface p-2 text-left active:scale-[0.98] ${selected ? 'border-accent-primary' : 'border-white/10'}`}>
                {selected && <span className="absolute right-2 top-2 z-10 rounded-full bg-accent-primary p-0.5 text-dark-primary"><Check className="h-3 w-3" /></span>}
                <span className="flex aspect-[3/2] w-full items-center justify-center rounded-lg bg-surface" style={raritySurfaceStyle(skin)}>{skin.img ? <img src={skin.img} alt="" loading="lazy" className="h-full w-full object-contain" /> : <Gem className="h-8 w-8 text-text-secondary" />}</span>
                <span className="mt-2 block min-h-9 w-full text-xs font-semibold leading-4 text-white">
                  <span className="block break-words">{title}</span>
                  {subtitleCompact && <span className="block break-words text-[11px] font-medium text-text-secondary">{subtitleCompact}</span>}
                </span>
                <span className="mt-auto inline-flex max-w-full items-center gap-1 pt-2 text-xs font-bold tabular-nums text-accent-primary"><Gem className="h-3 w-3 shrink-0" />{price}</span>
              </button>
            })}
          </div>
        </div>
      </div>
    </div>, document.body
  )
}

export default UpgradeSelectionSheet
